import React, { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Group, Transformer, Line } from 'react-konva';
import { Box, Button, IconButton, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import { HexColorPicker } from 'react-colorful';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, onSnapshot, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { fetchTemplateById } from '../utils/templateService';

interface BoardElement {
  id: string;
  type: 'image' | 'text' | 'sticky';
  content: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  fontSize?: number;
  rotation?: number;
}

const BOARD_WIDTH = 1600;
const BOARD_HEIGHT = 1000;
const GRID_SIZE = 20;

const ModernMoodBoardEditor: React.FC = () => {
  const [elements, setElements] = useState<BoardElement[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [color, setColor] = useState<string>("#FFD93D");
  const [canvasColor, setCanvasColor] = useState<string>("#f8fafc");
  const [canvasColorPicker, setCanvasColorPicker] = useState(false);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const transformerRef = useRef<any>(null);
  const stageRef = useRef<any>(null);
  const navigate = useNavigate();
  const { teamId, moodboardId } = useParams<{ teamId?: string; moodboardId: string }>();
  const [isTemplateMode, setIsTemplateMode] = useState<boolean>(!teamId);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [forking, setForking] = useState(false);

  // Smart guides state
  const [guides, setGuides] = useState<{x?: number, y?: number}[]>([]);

  // Undo/Redo state
  const [undoStack, setUndoStack] = useState<BoardElement[][]>([]);
  const [redoStack, setRedoStack] = useState<BoardElement[][]>([]);

  // Store loaded template for forking
  const [loadedTemplate, setLoadedTemplate] = useState<any>(null);

  // Firestore real-time sync
  useEffect(() => {
    if (!teamId || !moodboardId) return;
    const colRef = collection(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`);
    const unsub = onSnapshot(colRef, snap => {
      setElements(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as BoardElement)));
    });
    return () => unsub();
  }, [teamId, moodboardId]);

  // Load template if in template mode
  useEffect(() => {
    if (!isTemplateMode || !moodboardId) return;
    setTemplateLoaded(false);
    fetchTemplateById(moodboardId).then(template => {
      if (template) {
        setLoadedTemplate(template);
        // Convert TemplateElement[] to BoardElement[]
        setElements(template.elements.map(el => ({
          id: el.id,
          type: el.type === 'text' ? 'text' : 'image',
          content: el.type === 'text' ? el.text || '' : el.src || '',
          x: el.x,
          y: el.y,
          width: el.width || 180,
          height: el.height || 80,
          color: el.color,
          fontSize: el.fontSize,
          rotation: el.rotation,
        })));
      }
      setTemplateLoaded(true);
    });
  }, [isTemplateMode, moodboardId]);

  // Fork logic: on first edit in template mode
  const forkToTeamBoard = async (newElements: BoardElement[]) => {
    if (!teamId) return;
    setForking(true);
    // Use template fields if available
    const name = loadedTemplate?.name || 'Forked from template';
    const image = loadedTemplate?.preview_image_url || '';
    const creator = 'Current User';
    const createdAt = new Date();
    // Create new mood board doc in Firestore
    const colRef = collection(firestore, `teams/${teamId}/moodboards`);
    const docRef = await addDoc(colRef, {
      name,
      creator,
      createdAt,
      image,
    });
    // Add elements
    const elementsCol = collection(firestore, `teams/${teamId}/moodboards/${docRef.id}/elements`);
    for (const el of newElements) {
      const { id, ...rest } = el;
      await addDoc(elementsCol, rest);
    }
    setForking(false);
    // Redirect to team board editor
    navigate(`/teams/${teamId}/moodboards/${docRef.id}`);
  };

  // Track if first edit has happened
  const [hasForked, setHasForked] = useState(false);
  const forkIfNeeded = async (editFn: () => void) => {
    if (isTemplateMode && !hasForked && teamId) {
      await forkToTeamBoard(elements);
      setHasForked(true);
      return;
    }
    editFn();
  };

  // Helper to sync a full elements array to Firestore (overwrite all)
  const syncElementsToFirestore = async (newElements: BoardElement[]) => {
    if (!teamId || !moodboardId) return;
    // Delete all existing
    const colRef = collection(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`);
    const snap = await onSnapshot(colRef, () => {}); // dummy to get ref
    // Use getDocs to fetch all docs
    const { getDocs } = await import('firebase/firestore');
    const docs = await getDocs(colRef);
    for (const docSnap of docs.docs) {
      await deleteDoc(docSnap.ref);
    }
    // Add all new
    for (const el of newElements) {
      const { id, ...rest } = el;
      await setDoc(doc(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`, id), rest);
    }
  };

  // Wrap all element-changing actions to support undo/redo
  const pushUndo = () => {
    setUndoStack(stack => [...stack, elements]);
    setRedoStack([]);
  };

  // Add element (Firestore)
  const addElement = async (type: 'image' | 'text' | 'sticky', content?: string) => {
    await forkIfNeeded(() => {
      if (!teamId || !moodboardId) return;
      const colRef = collection(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`);
      const base: Omit<BoardElement, 'id'> = {
        type,
        content: content || (type === 'text' ? 'Double click to edit' : type === 'sticky' ? 'Sticky note' : ''),
        x: 100 + Math.random() * 200,
        y: 100 + Math.random() * 200,
        width: type === 'image' ? 200 : 180,
        height: type === 'image' ? 150 : 80,
        color: type === 'sticky' ? '#FFD93D' : type === 'text' ? '#222' : undefined,
        fontSize: type === 'text' ? 24 : type === 'sticky' ? 18 : undefined,
        rotation: 0,
      };
      return addDoc(colRef, base);
    });
  };

  // Move/resize/rotate element (Firestore)
  const updateElement = async (id: string, updates: Partial<BoardElement>) => {
    await forkIfNeeded(() => {
      if (!teamId || !moodboardId) return;
      const elRef = doc(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`, id);
      return updateDoc(elRef, updates);
    });
  };

  // Delete element (Firestore)
  const deleteElement = async (id: string) => {
    await forkIfNeeded(() => {
      if (!teamId || !moodboardId) return;
      const elRef = doc(firestore, `teams/${teamId}/moodboards/${moodboardId}/elements`, id);
      return deleteDoc(elRef);
    });
  };

  // Image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          addElement('image', event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Select, move, resize, rotate
  const handleSelect = (id: string, e: any) => {
    if (e.evt.shiftKey) {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    } else {
      setSelectedIds([id]);
    }
  };
  const handleDeselect = () => setSelectedIds([]);
  // Snap a value to the nearest grid
  const snap = (val: number) => Math.round(val / GRID_SIZE) * GRID_SIZE;
  const handleDrag = (id: string, x: number, y: number) => {
    // Snap to grid
    const snappedX = snap(x);
    const snappedY = snap(y);
    // Smart guides: check alignment with other elements
    const el = elements.find(e => e.id === id);
    if (el) {
      const movingCenterX = snappedX + el.width / 2;
      const movingCenterY = snappedY + el.height / 2;
      let newGuides: {x?: number, y?: number}[] = [];
      elements.forEach(other => {
        if (other.id === id) return;
        // Center align X
        const otherCenterX = other.x + other.width / 2;
        if (Math.abs(movingCenterX - otherCenterX) < 8) {
          newGuides.push({ x: otherCenterX });
        }
        // Center align Y
        const otherCenterY = other.y + other.height / 2;
        if (Math.abs(movingCenterY - otherCenterY) < 8) {
          newGuides.push({ y: otherCenterY });
        }
      });
      setGuides(newGuides);
    }
    updateElement(id, { x: snappedX, y: snappedY });
  };
  const handleTransform = (id: string, node: any) => {
    // Snap to grid
    const snappedX = snap(node.x());
    const snappedY = snap(node.y());
    const snappedWidth = snap(node.width() * node.scaleX());
    const snappedHeight = snap(node.height() * node.scaleY());
    updateElement(id, {
      x: snappedX,
      y: snappedY,
      width: snappedWidth,
      height: snappedHeight,
      rotation: node.rotation(),
    });
    node.scaleX(1);
    node.scaleY(1);
    setGuides([]);
  };
  const handleDelete = () => {
    selectedIds.forEach(id => deleteElement(id));
    setSelectedIds([]);
  };

  // Double-click to edit
  const handleDblClick = (el: BoardElement) => {
    if (el.type === 'text' || el.type === 'sticky') {
      setEditing({ id: el.id, value: el.content });
    }
  };
  const handleEditSave = async () => {
    await forkIfNeeded(async () => {
      if (editing) {
        await updateElement(editing.id, { content: editing.value });
        setEditing(null);
      }
    });
  };

  // Color picker for sticky/text
  const openColorPicker = () => {
    if (!selectedIds.length) return;
    const el = elements.find(e => e.id === selectedIds[0]);
    if (el && (el.type === 'text' || el.type === 'sticky')) {
      setColor(el.color || '#FFD93D');
      setColorPickerOpen(true);
    }
  };
  const handleColorChange = async (newColor: string) => {
    await forkIfNeeded(async () => {
      setColor(newColor);
      for (const id of selectedIds) {
        await updateElement(id, { color: newColor });
      }
    });
  };

  // Canvas color picker
  const handleCanvasColorChange = (newColor: string) => {
    setCanvasColor(newColor);
  };

  // Zoom and pan
  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const oldScale = scale;
    const pointer = stageRef.current.getPointerPosition();
    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale,
    };
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    setScale(newScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };
  const handleDragStage = (e: any) => {
    setStagePos({ x: e.target.x(), y: e.target.y() });
  };

  // Transformer logic
  useEffect(() => {
    if (transformerRef.current && selectedIds.length) {
      const stage = transformerRef.current.getStage();
      const nodes = selectedIds.map(id => stage.findOne(`#el-${id}`)).filter(Boolean);
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedIds, elements]);

  // Undo/Redo handlers
  const handleUndo = async () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(stack => stack.slice(0, -1));
    setRedoStack(stack => [...stack, elements]);
    setElements(prev);
    await syncElementsToFirestore(prev);
  };
  const handleRedo = async () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(stack => stack.slice(0, -1));
    setUndoStack(stack => [...stack, elements]);
    setElements(next);
    await syncElementsToFirestore(next);
  };

  // Render
  return (
    <Box sx={{ p: 2 }}>
      {/* Floating Toolbar */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 10 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => addElement('text')}>Add Text</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => addElement('sticky')}>Add Sticky</Button>
        <Button variant="contained" startIcon={<UploadFileIcon />} component="label">
          Add Image
          <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleImageUpload} />
        </Button>
        <Button variant="outlined" onClick={openColorPicker} disabled={!selectedIds.length} startIcon={<FormatColorFillIcon />}>Change Color</Button>
        <Button variant="outlined" onClick={() => setCanvasColorPicker(true)} startIcon={<FormatColorFillIcon />}>Canvas Color</Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedIds.length} startIcon={<DeleteIcon />}>Delete</Button>
        <Button variant="outlined" startIcon={<UndoIcon />} onClick={handleUndo} disabled={!undoStack.length}>Undo</Button>
        <Button variant="outlined" startIcon={<RedoIcon />} onClick={handleRedo} disabled={!redoStack.length}>Redo</Button>
      </Stack>
      {colorPickerOpen && (
        <Box sx={{ position: 'absolute', zIndex: 1000, top: 80, left: 40, bgcolor: 'white', p: 2, borderRadius: 2, boxShadow: 3 }}>
          <HexColorPicker color={color} onChange={handleColorChange} />
          <Button sx={{ mt: 1 }} onClick={() => setColorPickerOpen(false)}>Close</Button>
        </Box>
      )}
      {canvasColorPicker && (
        <Box sx={{ position: 'absolute', zIndex: 1000, top: 80, left: 200, bgcolor: 'white', p: 2, borderRadius: 2, boxShadow: 3 }}>
          <HexColorPicker color={canvasColor} onChange={handleCanvasColorChange} />
          <Button sx={{ mt: 1 }} onClick={() => setCanvasColorPicker(false)}>Close</Button>
        </Box>
      )}
      <Stage
        ref={stageRef}
        width={BOARD_WIDTH}
        height={BOARD_HEIGHT}
        scaleX={scale}
        scaleY={scale}
        x={stagePos.x}
        y={stagePos.y}
        style={{ background: canvasColor, borderRadius: 12, boxShadow: '0 2px 8px #0001', cursor: 'grab' }}
        onWheel={handleWheel}
        draggable
        onDragEnd={handleDragStage}
        onMouseDown={e => {
          // Deselect if click on empty area
          if (e.target === e.target.getStage()) handleDeselect();
        }}
        onMouseUp={() => setGuides([])}
      >
        <Layer>
          {elements.map(el => {
            if (el.type === 'text') {
              return (
                <Group
                  key={el.id}
                  id={`el-${el.id}`}
                  x={el.x}
                  y={el.y}
                  draggable
                  rotation={el.rotation || 0}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onClick={e => handleSelect(el.id, e)}
                  onDblClick={() => handleDblClick(el)}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                >
                  <KonvaText
                    text={el.content}
                    fontSize={el.fontSize || 24}
                    fill={el.color || '#222'}
                    width={el.width}
                    height={el.height}
                  />
                </Group>
              );
            }
            if (el.type === 'sticky') {
              return (
                <Group
                  key={el.id}
                  id={`el-${el.id}`}
                  x={el.x}
                  y={el.y}
                  draggable
                  rotation={el.rotation || 0}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onClick={e => handleSelect(el.id, e)}
                  onDblClick={() => handleDblClick(el)}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                >
                  <Rect
                    width={el.width}
                    height={el.height}
                    fill={el.color || '#FFD93D'}
                    cornerRadius={12}
                  />
                  <KonvaText
                    text={el.content}
                    fontSize={el.fontSize || 18}
                    fill="#222"
                    width={el.width}
                    height={el.height}
                    padding={10}
                  />
                </Group>
              );
            }
            if (el.type === 'image') {
              const img = new window.Image();
              img.src = el.content;
              return (
                <Group
                  key={el.id}
                  id={`el-${el.id}`}
                  x={el.x}
                  y={el.y}
                  draggable
                  rotation={el.rotation || 0}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onClick={e => handleSelect(el.id, e)}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                >
                  <KonvaImage
                    image={img}
                    width={el.width}
                    height={el.height}
                  />
                </Group>
              );
            }
            return null;
          })}
          {/* Smart guides rendering */}
          {guides.map((g, i) =>
            g.x !== undefined ? (
              <Line
                key={`guide-x-${i}`}
                points={[g.x, 0, g.x, BOARD_HEIGHT]}
                stroke="#1976d2"
                strokeWidth={1}
                dash={[8, 4]}
              />
            ) : g.y !== undefined ? (
              <Line
                key={`guide-y-${i}`}
                points={[0, g.y, BOARD_WIDTH, g.y]}
                stroke="#1976d2"
                strokeWidth={1}
                dash={[8, 4]}
              />
            ) : null
          )}
          {/* Transformer for multi-select */}
          <Transformer
            ref={transformerRef}
            nodes={[]}
            rotateEnabled
            enabledAnchors={["top-left","top-right","bottom-left","bottom-right"]}
          />
        </Layer>
      </Stage>
      {/* Edit dialog for text/sticky */}
      <Dialog open={!!editing} onClose={() => setEditing(null)}>
        <DialogTitle>Edit Content</DialogTitle>
        <DialogContent>
          <TextField
            value={editing?.value || ''}
            onChange={e => setEditing(editing ? { ...editing, value: e.target.value } : null)}
            fullWidth
            autoFocus
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModernMoodBoardEditor; 